/**
 * TOSS Web Application — Client portal: the notifications popover (wide screens only)
 *
 * On a phone the bell navigates to a full-screen feed with its own app bar. On a wide screen the
 * same feed drops out of the bell as a panel (PO decision): there is room for it beside the page,
 * and a customer checking «что нового» in the middle of filling a form should not lose the form to
 * find out.
 *
 * Since 2026-08-26 the panel itself is `NotificationsPopover` from the shared top-bar layer — the
 * internal application draws the same one. This file is the portal's HALF of that: it turns
 * `ClientNotificationDto` into rows of three strings, and it owns what a click does. The shared
 * panel never sees a notification, which is the point: the two DTOs behind those rows are not
 * interchangeable, and the internal one carries entity ids and a staff-side type this audience
 * must never be shown.
 *
 * Where a notification leads and how its age reads live in `clientNotificationPresentation.ts`,
 * outside both this file and the page — the client-portal boundary (decision Р6) makes
 * `features/client/**` unimportable from here, and its own message says shared logic belongs
 * outside it in the first place.
 *
 * Clicking a row marks it read and navigates, which also closes the panel: the route change
 * unmounts the portal.
 */

import type { RefObject } from 'react';
import { useNavigate } from 'react-router-dom';
import { NotificationsPopover, type NotificationRowView } from '@/components/topbar';
import { ROUTES } from '@/constants/routes';
import type { ClientNotificationDto } from '@/api/clientPortal/clientPortal.types';
import {
  clientNotificationTarget,
  formatClientNotificationAge,
} from '@/hooks/api/clientPortal/clientNotificationPresentation';

// ============================================================================
// Types
// ============================================================================

export interface ClientNotificationsPopoverProps {
  isOpen: boolean;
  onClose: () => void;
  triggerRef: RefObject<HTMLElement | null>;
  notifications: ClientNotificationDto[];
  isLoading: boolean;
  unreadCount: number;
  onMarkAll: () => void;
  isMarkingAll: boolean;
  /** Mark one notification read — called when its row is clicked while unread. */
  onMarkRead: (id: string) => void;
}

// ============================================================================
// Component
// ============================================================================

export function ClientNotificationsPopover({
  isOpen,
  onClose,
  triggerRef,
  notifications,
  isLoading,
  unreadCount,
  onMarkAll,
  isMarkingAll,
  onMarkRead,
}: ClientNotificationsPopoverProps): JSX.Element {
  const navigate = useNavigate();

  const items: NotificationRowView[] = notifications.map((notification) => ({
    id: notification.id,
    title: notification.title,
    meta: notification.content
      ? `${notification.content} · ${formatClientNotificationAge(notification.createdAt)}`
      : formatClientNotificationAge(notification.createdAt),
    isRead: notification.isRead,
  }));

  const activate = (id: string): void => {
    const notification = notifications.find((candidate) => candidate.id === id);
    if (!notification) return;

    if (!notification.isRead) onMarkRead(notification.id);
    const target = clientNotificationTarget(notification);
    if (target) navigate(target);
    onClose();
  };

  return (
    <NotificationsPopover
      isOpen={isOpen}
      onClose={onClose}
      triggerRef={triggerRef}
      items={items}
      isLoading={isLoading}
      hasUnread={unreadCount > 0}
      onMarkAll={onMarkAll}
      isMarkingAll={isMarkingAll}
      onActivate={activate}
      allHref={ROUTES.client.notifications}
    />
  );
}

export default ClientNotificationsPopover;
